import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import GoogleLoginButton from '../components/GoogleLoginButton';
import { Loading } from '../components';
import styles from './Login.module.css';
import logo from '../assets/images/logo.svg';

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const { login, loginWithGoogle, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/dashboard';

  useEffect(() => {
    if (user) {
      navigate(from, { replace: true });
    }
  }, [user, navigate, from]);

  const handleGoogleSuccess = async (token: string) => {
    setIsLoading(true);
    setError('');

    try {
      await loginWithGoogle(token);
      navigate(from, { replace: true });
    } catch (error: any) {
      console.error('Erro no login com Google:', error);
      if (error.response?.status === 401) {
        setError('Falha na autenticação com Google. Verifique suas permissões.');
      } else {
        setError('Erro interno do servidor. Tente novamente.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = (error: string) => {
    setError(`Erro no login com Google: ${error}`);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!username || !email || !password) {
      setError('Por favor, preencha todos os campos');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await login({ username, email, password });
      navigate(from, { replace: true });
    } catch (error: any) {
      console.error('Erro no login:', error);
      if (error.response?.status === 401) {
        setError('Credenciais inválidas. Verifique seu usuário e senha.');
      } else {
        setError('Erro interno do servidor. Tente novamente.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.loginContainer}>
      <div className={styles.loginCard}>
        <div className={styles.logoContainer}>
          <img src={logo} alt="999 Viagens" className={styles.logo} />
        </div>
        
        <h1 className={styles.title}>Backoffice</h1>
        <p className={styles.subtitle}>Sistema de gestão da 999 Viagens</p>
        
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label htmlFor="username" className={styles.label}>
              Usuário
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className={styles.input}
              placeholder="Digite seu usuário"
              required
              disabled={isLoading}
              autoComplete="username"
            />
          </div>
          
          <div className={styles.formGroup}>
            <label htmlFor="email" className={styles.label}>
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={styles.input}
              placeholder="Digite seu email"
              required
              disabled={isLoading}
              autoComplete="email"
            />
          </div>
          
          <div className={styles.formGroup}>
            <label htmlFor="password" className={styles.label}>
              Senha
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={styles.input}
              placeholder="Digite sua senha"
              required
              disabled={isLoading}
              autoComplete="current-password"
            />
          </div>
          
          <button
            type="submit"
            disabled={isLoading}
            className={styles.submitButton}
          >
            {isLoading ? <Loading size="small" text="" /> : 'Entrar'}
          </button>
          
          {error && (
            <div className={styles.errorMessage}>
              {error}
            </div>
          )}
        </form>
        
        <div className={styles.divider}>
          <span className={styles.dividerText}>ou</span>
        </div>
        
        <GoogleLoginButton
          onSuccess={handleGoogleSuccess}
          onError={handleGoogleError}
          disabled={isLoading}
        />
        
        <div className={styles.footer}>
          <span className={styles.companyName}>999 Viagens</span> - Backoffice
        </div>
      </div>
    </div>
  );
};

export default Login;